// Editorial primitive: collapsible reference block.
// Sentence-case label trigger + rotating chevron + optional preview when
// closed. Opt-in localStorage persistence via `storageKey` — caller-provided,
// never auto-generated. Convention: `{page}-{id}-{section}` (see
// apps/dashboard/CLAUDE.md).

import { useEffect, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export interface CollapsibleSectionProps {
  /** Sentence-case label rendered in the trigger. */
  label: string;
  /** Arbitrary children rendered in the open content region. */
  children: ReactNode;
  /** When set, persists open/closed state to `localStorage` under this key. */
  storageKey?: string;
  /** Muted one-line preview rendered below the trigger when closed. */
  previewText?: string;
  /** Initial open state before localStorage is consulted. Defaults to `false`. */
  defaultOpen?: boolean;
  /** Optional aria-label for the section container. Defaults to `label`. */
  ariaLabel?: string;
  className?: string;
}

function readStoredOpen(
  storageKey: string | undefined,
  fallback: boolean,
): boolean {
  if (!storageKey) return fallback;
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(storageKey);
    if (value === null) return fallback;
    return value === "true";
  } catch {
    return fallback;
  }
}

export function CollapsibleSection({
  label,
  children,
  storageKey,
  previewText,
  defaultOpen = false,
  ariaLabel,
  className,
}: CollapsibleSectionProps) {
  // Hydrate synchronously in the initializer. This is a Vite SPA — `window` is
  // always defined on the client — and reading in the initializer avoids a
  // two-effect dance where the write effect could clobber a stored value
  // before the read effect re-applied it. `readStoredOpen` still falls back
  // to `defaultOpen` if `localStorage` throws (private mode, quota, SSR).
  const [open, setOpen] = useState(() =>
    readStoredOpen(storageKey, defaultOpen),
  );

  useEffect(() => {
    if (!storageKey) return;
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, String(open));
    } catch {
      // localStorage may throw in private-mode / quota scenarios — swallow.
    }
  }, [open, storageKey]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <section
        data-slot="collapsible-section"
        aria-label={ariaLabel ?? label}
        className={cn(
          "rounded-lg border border-border/60 bg-muted/30 px-5 py-4",
          className,
        )}
      >
        <CollapsibleTrigger className="group flex w-full items-center gap-2 text-left">
          <ChevronRight
            aria-hidden="true"
            className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90"
          />
          <span className="text-sm font-medium text-foreground">{label}</span>
        </CollapsibleTrigger>
        {!open && previewText && (
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {previewText}
          </p>
        )}
        <CollapsibleContent className="mt-4">{children}</CollapsibleContent>
      </section>
    </Collapsible>
  );
}
